import { useMemo, useState } from "react"
import { TextInput, View } from "react-native"
import Base from "../../components/layout/Base"
import MainScrollPanel from "../../components/layout/MainScrollPanel"
import {
	useAddUserToUserGroupMutation,
	useAdminUserGroupsQuery,
	useAllowLockersystemAccessMutation,
	useCreateUserGroupMutation,
	useDeleteUserGroupMutation,
	useDenyLockersystemAccessMutation,
	useLockersystemUsersQuery,
	useRemoveUserFromUserGroupMutation
} from "./queries.generated"
import { CheckBox, SearchBar } from "react-native-elements"
import { useTranslation } from "react-i18next"
import { Text, TouchButton } from "pusatec-react-native"
import { theme } from "../../theme"

const isDefined = <T,>(value: T | null | undefined): value is T => value != null

const CreateGroupForm = (props: {
	lockersystemId: string
	onCancel: () => void
	onCreated: () => void
}) => {
	const { t } = useTranslation()
	const [name, setName] = useState("")
	const [createUserGroup] = useCreateUserGroupMutation()

	return (
		<Base>
			<Text text={t("New user group")} bold />
			<TextInput
				value={name}
				onChangeText={setName}
				style={{
					borderColor: "grey",
					borderWidth: 1,
					height: 40,
					marginTop: 10,
					paddingHorizontal: 8,
					backgroundColor: "white",
				}}
				placeholder={t("User group name")}
			/>
			<View style={{ flexDirection: "row", marginTop: 10, justifyContent: "space-between" }}>
				<TouchButton text={t("Cancel")} onPress={props.onCancel} />
				<TouchButton
					text={t("Create")}
					disabled={!name.trim()}
					onPress={async () => {
						await createUserGroup({
							variables: {
								input: {
									name: name.trim(),
									lockersystemId: props.lockersystemId
								}
							}
						})
						setName("")
						props.onCreated()
					}}
				/>
			</View>
		</Base>
	)
}

export const Users = (props: {
	lockersystemId: string
}) => {
	const [expanded, setExpanded] = useState<string | null>(null)
	const [searchWord, setSearchWord] = useState("")
	const [creatingGroup, setCreatingGroup] = useState(false)
	const { t } = useTranslation()

	const [denyLockersystemAccess] = useDenyLockersystemAccessMutation()
	const [allowLockersystemAccess] = useAllowLockersystemAccessMutation()
	const [addUserToUserGroup] = useAddUserToUserGroupMutation()
	const [removeUserFromUserGroup] = useRemoveUserFromUserGroupMutation()
	const [deleteUserGroup] = useDeleteUserGroupMutation()

	const usersQuery = useLockersystemUsersQuery({
		variables: {
			query: {
				lockersystemId: props.lockersystemId,
				search: searchWord || undefined
			}
		}
	})

	const groupsQuery = useAdminUserGroupsQuery()

	const users = useMemo(() => {
		const list = usersQuery.data?.lockersystemUserPermissions.filter(user => user.user?.email) ?? []
		return [...list].sort((a, b) => (a.user?.name || a.user?.email || "").localeCompare(b.user?.name || b.user?.email || ""))
	}, [usersQuery.data?.lockersystemUserPermissions])

	const groups = useMemo(() => {
		return (groupsQuery.data?.userGroups ?? [])
			.filter(isDefined)
			.filter(group => group?.lockersystemId === props.lockersystemId)
			.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
	}, [groupsQuery.data?.userGroups, props.lockersystemId])

	return (
		<View style={{ flex: 1 }}>
			<SearchBar
				lightTheme
				inputStyle={theme.Input.inputStyle}
				errorStyle={theme.Input.errorMessageStyle}
				labelStyle={theme.Input.labelStyle}
				containerStyle={theme.Input.containerStyle}
				inputContainerStyle={theme.Input.inputContainerStyle}
				value={searchWord}
				onChangeText={text => setSearchWord(text)}
			/>

			{creatingGroup && (
				<CreateGroupForm
					lockersystemId={props.lockersystemId}
					onCancel={() => setCreatingGroup(false)}
					onCreated={() => {
						setCreatingGroup(false)
						groupsQuery.refetch()
					}}
				/>
			)}

			<MainScrollPanel
				style={{ borderWidth: 0, backgroundColor: undefined }}
				onRefresh={() => {
					usersQuery.refetch()
					groupsQuery.refetch()
				}}
				refreshing={usersQuery.loading || groupsQuery.loading}
			>
				<Text text={t("User groups")} bold style={{ marginVertical: 10, textAlign: "center" }} />
				{groups.map(group => (
					<Base key={group.id}>
						<Text text={group.name} bold />
						<Text text={t("{{count}} users", { count: group.users.length })} />
						{group.users.filter(isDefined).map(user => (
							<Text
								key={user.id}
								text={user.name || user.email || "-"}
								style={{ marginTop: 4 }}
							/>
						))}
						<View style={{ marginTop: 10 }}>
							<TouchButton
								type="secondary"
								text={t("Delete user group")}
								onPress={async () => {
									await deleteUserGroup({
										variables: {
											userGroupId: group.id
										}
									})
									groupsQuery.refetch()
								}}
							/>
						</View>
					</Base>
				))}

				<Text text={t("Users")} bold style={{ marginVertical: 10, textAlign: "center" }} />
				{users.map(permission => {
					let borderRightColor = "white"
					if (permission.accessDenied) borderRightColor = "red"
					const userGroups = groups.filter(group => group.users.filter(isDefined).some(user => user.id === permission.user?.id))

					return (
						<Base
							key={permission.id}
							onPress={() => setExpanded(permission.id === expanded ? null : permission.id as string)}
							selected={permission.id === expanded}
							style={{ borderRightColor, borderRightWidth: 8 }}
						>
							<Text type="strong" text={permission.user?.name || permission.user?.email} />
							{expanded === permission.id && (
								<>
									<Text text={permission.user?.email} />
									<CheckBox
										checked={permission.accessDenied === true}
										title={t("Access Denied")}
										onPress={() => {
											if (!permission.accessDenied) {
												denyLockersystemAccess({
													variables: {
														input: {
															userId: permission.user?.id as string,
															lockersystemId: props.lockersystemId
														}
													}
												})
												return
											}
											allowLockersystemAccess({
												variables: {
													input: {
														userId: permission.user?.id as string,
														lockersystemId: props.lockersystemId
													}
												}
											})
										}}
									/>
									<Text text={t("User groups")} bold style={{ marginTop: 10 }} />
									{groups.map(group => {
										const inGroup = userGroups.some(userGroup => userGroup.id === group.id)
										return (
											<Base
												key={group.id}
												selected={inGroup}
												onPress={async () => {
													const variables = {
														input: {
															userGroupId: group.id,
															userId: permission.user?.id as string
														}
													}
													if (inGroup) {
														await removeUserFromUserGroup({ variables })
													} else {
														await addUserToUserGroup({ variables })
													}
													groupsQuery.refetch()
												}}
												style={{ padding: 12 }}
											>
												<Text text={group.name} />
											</Base>
										)
									})}
								</>
							)}
						</Base>
					)
				})}
			</MainScrollPanel>

			<View style={{ padding: 10 }}>
				<TouchButton text={t("Create user group")} onPress={() => setCreatingGroup(true)} />
			</View>
		</View>
	)
}
